import LiveTrackingIllustration from "../../assets/live-match-tracking.avif";
import DetailedStatsIllustration from "../../assets/detailed-statistics.avif";
import DotsPatternBackground from "../../assets/dots-bg.svg";
import { TitleBadge } from "./TitleBadge";
import { motion } from "framer-motion";
import { TypewriterText } from "./Typewriter";

type Highlight = {
  title: string;
  description: string;
  image: string;
  imageAlt: string;
  imageLeftOnDesktop?: boolean;
  withDotsBackdrop?: boolean;
};

const HIGHLIGHTS: Highlight[] = [
  {
    title: "Live Match Tracking",
    description:
      "Follow every play live. Track events instantly and see actions evolve without delay so your viewers always stay ahead of the game.",
    image: LiveTrackingIllustration,
    imageAlt: "Vector pitch showing the current attack zone",
    imageLeftOnDesktop: true,
    withDotsBackdrop: true,
  },
  {
    title: "Detailed Statistics",
    description:
      "Team dashboards, player comparisons and possession charts update automatically so analysts can focus on insights.",
    image: DetailedStatsIllustration,
    imageAlt: "Stats dashboard cards with gauges and bars",
  },
];

export const Highlights = () => (
  <section className="flex mx-3 md:mx-5 lg:mx-10 2xl:mx-20 py-20 lg:py-36 flex-col gap-20 lg:gap-36 text-white overflow-x-hidden w-[calc(100vw-1.5rem)] md:w-[calc(100vw-2.5rem)] lg:w-[calc(100vw-5rem)] 2xl:w-[calc(100vw-10rem)] max-w-full">
    {HIGHLIGHTS.map(
      (
        {
          title,
          description,
          image,
          imageAlt,
          imageLeftOnDesktop,
          withDotsBackdrop,
        },
        index
      ) => {
        const isImageFirst = Boolean(imageLeftOnDesktop);
        return (
          <article
            key={title}
            className={`flex flex-col gap-10 lg:gap-20 justify-between items-center ${
              isImageFirst ? "lg:flex-row" : "lg:flex-row-reverse"
            }`}
          >
            <motion.div
              className="flex flex-1 flex-col gap-4 lg:gap-7 text-center lg:text-left lg:px-10"
              initial={{
                x: isImageFirst ? -120 : 120,
                opacity: 0,
              }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.9, ease: "easeOut" }}
            >
              <TitleBadge title={title} />
              <TypewriterText
                text={description}
                className="break-words text-gray-200 text-sm lg:text-xl"
                startDelayMs={200}
                charIntervalMs={18}
              />
            </motion.div>
            <div className="flex flex-1 items-center justify-center">
              {withDotsBackdrop ? (
                <motion.div
                  className="relative w-full overflow-hidden lg:aspect-[16/7]"
                  initial={{ opacity: 0, scale: 0.96, y: 24 }}
                  whileInView={{ opacity: 1, scale: 1, y: 0 }}
                  whileHover={{ scale: 1.03 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.7, ease: "easeOut" }}
                >
                  <img
                    src={DotsPatternBackground}
                    alt=""
                    aria-hidden
                    className="absolute top-0 left-0 w-full h-full object-cover opacity-30 lg:bottom-0 lg:top-auto lg:h-[130%]"
                  />
                  <img
                    src={image}
                    alt={imageAlt}
                    className="relative z-10 w-full object-contain lg:absolute lg:bottom-0 lg:left-0 lg:object-bottom"
                    loading={index === 0 ? "eager" : "lazy"}
                  />
                </motion.div>
              ) : (
                <motion.img
                  src={image}
                  alt={imageAlt}
                  className="w-full object-contain"
                  loading={index === 0 ? "eager" : "lazy"}
                  initial={{ opacity: 0, scale: 0.96, y: 24 }}
                  whileInView={{ opacity: 1, scale: 1, y: 0 }}
                  whileHover={{ scale: 1.03 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.7, ease: "easeOut" }}
                />
              )}
            </div>
          </article>
        );
      }
    )}
  </section>
);
